/**
 * Visual Registry Sync — Runtime Registry ↔ Governance Map Consistency Check
 *
 * RESOLVES: CONFLICT-02 (Visual Registry vs. VISUAL_GOVERNANCE_MAP.json)
 *
 * Provides utilities to:
 * 1. Check if the runtime visual registry and governance map are in sync
 * 2. Generate a governance map from the runtime registry
 * 3. Report discrepancies for the developer diagnostics panel
 *
 * This ensures that new visual types added to the registry are automatically
 * recognized by the governance pipeline without manual JSON updates.
 */

import { VISUAL_REGISTRY } from '../../visuals/registry/visualRegistry'
import { VisualAidType } from '../../visuals/types'
import { debug, warn } from '../../utils/logger'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VisualRegistrySyncReport {
  /** Whether registry and governance map are in sync */
  inSync: boolean

  /** Visual types in the runtime registry */
  registryTypes: string[]

  /** Visual types in the governance map */
  governanceMapTypes: string[]

  /** Types in registry but NOT in governance map (new, unreviewed) */
  missingFromGovernance: string[]

  /** Types in governance map but NOT in registry (stale, removed) */
  missingFromRegistry: string[]

  /** Types present in both (healthy) */
  synchronized: string[]

  /** Timestamp of check */
  checkedAt: string
}

// ─── Sync Check ───────────────────────────────────────────────────────────────

/**
 * Check synchronization between the runtime visual registry and governance map.
 *
 * @param governanceMapTypes - Array of visual type strings from VISUAL_GOVERNANCE_MAP.json
 * @returns Sync report with discrepancies
 */
export function checkVisualRegistrySync(
  governanceMapTypes: string[],
): VisualRegistrySyncReport {
  const registryTypes = Object.keys(VISUAL_REGISTRY) as string[]
  const registrySet = new Set(registryTypes)
  const governanceSet = new Set(governanceMapTypes)

  const missingFromGovernance = registryTypes.filter(
    (type) => !governanceSet.has(type),
  )
  const missingFromRegistry = governanceMapTypes.filter(
    (type) => !registrySet.has(type),
  )
  const synchronized = registryTypes.filter((type) => governanceSet.has(type))

  const inSync =
    missingFromGovernance.length === 0 && missingFromRegistry.length === 0

  if (!inSync) {
    if (missingFromGovernance.length > 0) {
      warn(
        `🔴 Visual Registry Sync: ${missingFromGovernance.length} types in registry but NOT in governance map: ${missingFromGovernance.join(', ')}`,
      )
    }
    if (missingFromRegistry.length > 0) {
      warn(
        `🟡 Visual Registry Sync: ${missingFromRegistry.length} types in governance map but NOT in registry: ${missingFromRegistry.join(', ')}`,
      )
    }
  } else {
    debug(
      `✅ Visual Registry Sync: All ${synchronized.length} types are in sync`,
    )
  }

  return {
    inSync,
    registryTypes,
    governanceMapTypes,
    missingFromGovernance,
    missingFromRegistry,
    synchronized,
    checkedAt: new Date().toISOString(),
  }
}

/**
 * Generate a governance-compatible type list from the runtime registry.
 * Use this to auto-generate or update VISUAL_GOVERNANCE_MAP.json.
 */
export function generateGovernanceMapFromRegistry(): Record<
  string,
  {
    componentName: string
    isRegistered: true
  }
> {
  const map: Record<string, { componentName: string; isRegistered: true }> = {}

  for (const [type, component] of Object.entries(VISUAL_REGISTRY)) {
    map[type] = {
      componentName: component.displayName || component.name || type,
      isRegistered: true,
    }
  }

  return map
}

/**
 * Get all registered visual types from the runtime registry.
 * This is the authoritative source — governance map should match this.
 */
export function getRegisteredVisualTypes(): VisualAidType[] {
  return Object.keys(VISUAL_REGISTRY) as VisualAidType[]
}

/**
 * Check if a visual type is registered in the runtime registry.
 * Use this instead of checking VISUAL_GOVERNANCE_MAP.json for runtime validation.
 */
export function isVisualTypeRegistered(type: string): boolean {
  return type in VISUAL_REGISTRY
}
